/**
 * Payment Intents API
 * Client for booking request payment intent endpoints
 * Follows the same pattern as the generated client in packages/libs/api
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  UseMutationOptions,
} from '@tanstack/react-query';

import { legacyApiInstance, type ErrorType } from './legacyApiInstance';

// Type definitions
export interface CreatePaymentIntentRequest {
  amount_cents: number;
  currency?: string;
}

export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  managed_account_token?: string;
  is_connected_account?: boolean;
}

export interface PaymentIntentDetails {
  id: string;
  status: string;
  amount: number;
  currency: string;
  client_secret: string;
}

// Error type for payment intent operations
export interface PaymentIntentError {
  error: string;
}

/**
 * Create payment intent
 */
export const createPaymentIntent = (
  bookingRequestId: string,
  createPaymentIntentRequest: CreatePaymentIntentRequest
) => {
  return legacyApiInstance<PaymentIntentResponse>({
    url: `/api/booking_requests/${bookingRequestId}/payment_intents`,
    method: 'POST',
    data: {
      amount_cents: createPaymentIntentRequest.amount_cents,
      currency: createPaymentIntentRequest.currency || 'usd',
    },
  });
};

export const getCreatePaymentIntentMutationOptions = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    TError,
    { bookingRequestId: string; data: CreatePaymentIntentRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPaymentIntent>>,
  TError,
  { bookingRequestId: string; data: CreatePaymentIntentRequest },
  TContext
> => {
  const mutationKey = ['createPaymentIntent'];
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    { bookingRequestId: string; data: CreatePaymentIntentRequest }
  > = (props) => {
    const { bookingRequestId, data } = props ?? {};
    return createPaymentIntent(bookingRequestId, data);
  };

  return { mutationKey, mutationFn, ...mutationOptions };
};

export type CreatePaymentIntentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPaymentIntent>>
>;
export type CreatePaymentIntentMutationBody = CreatePaymentIntentRequest;
export type CreatePaymentIntentMutationError = ErrorType<PaymentIntentError>;

/**
 * Hook to create payment intent
 */
export const useCreatePaymentIntent = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    TError,
    { bookingRequestId: string; data: CreatePaymentIntentRequest },
    TContext
  >;
}) => {
  const mutationOptions = getCreatePaymentIntentMutationOptions(options);

  return useMutation(mutationOptions);
};
